import { ThinkingOrb } from 'thinking-orbs';

import type { AdminKpisResponse } from '@/types/admin';

export function KpiCards({
  data,
  isLoading,
  isError,
}: {
  data?: AdminKpisResponse;
  isLoading: boolean;
  isError: boolean;
}) {
  if (isError) {
    return (
      <div className="p-16 border border-line rounded-lg bg-raised text-ink-muted">
        Failed to load KPIs.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16">
      <KpiCard
        title="Active subscriptions"
        value={data ? data.active_subscriptions.toLocaleString() : '—'}
        sublabel={
          data && data.cp_active_subscriptions != null && data.portal_active_subscriptions != null
            ? `${data.cp_active_subscriptions} CP · ${data.portal_active_subscriptions} Portal`
            : undefined
        }
        loading={isLoading}
      />
      <KpiCard
        title="New subscribers · last 7 days"
        value={data ? data.signups_last_7d.toLocaleString() : '—'}
        sublabel={
          data ? `${data.signups_last_30d.toLocaleString()} in last 30d` : undefined
        }
        loading={isLoading}
      />
      <KpiCard
        title="Revenue · last 7 days"
        value={data ? formatCents(data.revenue_last_7d_cents) : '—'}
        sublabel={
          data ? `${formatCents(data.revenue_last_30d_cents)} in last 30d` : undefined
        }
        loading={isLoading}
      />
      <KpiCard
        title="Renewal payment failure rate · 30d"
        value={
          data ? `${data.subscription_cycle_failure_rate_pct.toFixed(1)}%` : '—'
        }
        sublabel={
          data
            ? `${data.subscription_cycle_orders_30d.toLocaleString()} cycle orders`
            : undefined
        }
        loading={isLoading}
      />
    </div>
  );
}

function KpiCard({
  title,
  value,
  sublabel,
  loading,
}: {
  title: string;
  value: string;
  sublabel?: string;
  loading?: boolean;
}) {
  return (
    <div className="p-16 border border-line rounded-lg bg-raised">
      <p className="type-label-sm text-ink-muted">{title}</p>
      <p
        className={`type-heading-lg mt-6 ${loading ? 'text-ink-muted' : ''}`}
      >
        {loading ? (
          // Inline 20px preset — its own tuned design, not a shrunk 64.
          // theme pinned for the same reason as LoadingRow (light-only UI).
          <ThinkingOrb
            state="solving"
            size={20}
            theme="light"
            aria-label="Loading"
            style={{ display: 'inline-block', verticalAlign: 'middle' }}
          />
        ) : (
          value
        )}
      </p>
      {sublabel && (
        <p className="type-label-sm text-ink-muted mt-4">{sublabel}</p>
      )}
    </div>
  );
}

function formatCents(cents: number): string {
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(dollars);
}
